import React, { useState, useRef } from 'react';
import { 
  Book, 
  ChevronDown, 
  ChevronRight, 
  PlayCircle,
  FileText,
  Settings,
  Users,
  BarChart3,
  MessageSquare,
  Lightbulb,
  Target,
  Building2,
  Shield,
  Layers,
  TrendingUp,
  HelpCircle,
  CheckCircle2,
  AlertCircle,
  Rocket,
  Download,
  Key,
  Cloud,
  Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Accordion component for expandable sections
const AccordionItem = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-700 rounded-lg mb-3 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-slate-800/50 hover:bg-slate-800 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          <span className="font-medium text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-slate-900/50 text-slate-300 text-sm leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
};

// Step component for tutorials
const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center">
      <span className="text-blue-400 font-bold text-sm">{number}</span>
    </div>
    <div className="flex-1">
      <h4 className="font-medium text-white mb-1">{title}</h4>
      <div className="text-slate-400 text-sm">{children}</div>
    </div>
  </div>
);

// Feature card component
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-4">
    <div className="flex items-center gap-3 mb-2">
      <div className="p-2 bg-blue-500/10 rounded-lg">
        <Icon className="w-5 h-5 text-blue-400" />
      </div>
      <h4 className="font-medium text-white">{title}</h4>
    </div>
    <p className="text-slate-400 text-sm">{description}</p>
  </div>
);

// Checklist Item component
const ChecklistItem = ({ number, title, description, subItems = [] }) => (
  <div className="border-l-2 border-blue-500/50 pl-4 pb-4 mb-2">
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center mt-0.5">
        <span className="text-blue-400 font-bold text-xs">{number}</span>
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-white text-base">{title}</h4>
        <p className="text-slate-400 text-sm mt-1">{description}</p>
        {subItems.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {subItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">{item}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </div>
);

// Checklist data for PDF and display
const checklistData = [
  {
    number: "1",
    title: "Configurar Llaves de API (LLM Keys)",
    description: "Configura las credenciales de los proveedores de IA que usarán los agentes.",
    subItems: [
      "Ir a Configuración → API Keys",
      "Agregar API Key de OpenAI (GPT-4, GPT-3.5)",
      "Agregar API Key de Anthropic (Claude) si se requiere",
      "Agregar API Key de Google (Gemini) si se requiere",
      "Verificar que las llaves estén activas y con saldo disponible",
      "Configurar límites de uso por tenant si aplica"
    ]
  },
  {
    number: "2",
    title: "Configurar Almacenamiento AWS S3",
    description: "Configura el bucket de S3 para almacenar archivos de ingesta y reportes.",
    subItems: [
      "Ir a Configuración → Storage Settings",
      "Ingresar AWS Access Key ID",
      "Ingresar AWS Secret Access Key",
      "Especificar nombre del Bucket S3",
      "Configurar la región del bucket (ej: us-east-1)",
      "Verificar permisos de lectura/escritura en el bucket",
      "Probar la conexión con archivo de prueba"
    ]
  },
  {
    number: "3",
    title: "Configurar Taxonomía de Industria",
    description: "Define la estructura jerárquica de industrias que los tenants podrán seleccionar.",
    subItems: [
      "Ir a Industry Gateway → Taxonomía de Industrias",
      "Crear industria principal (ej: Retail, Tecnología, Manufactura)",
      "Agregar Modelos de Negocio (ej: B2C, B2B, SaaS, Franquicia)",
      "Agregar Estructuras Operativas (ej: Tienda Física, E-commerce, Distribución)",
      "Agregar Categorías dentro de cada Estructura (ej: Ventas, Logística, Marketing)",
      "Opcionalmente: Usar carga masiva con Excel (Plantilla: Industria, Modelo_Negocio, Estructura_Operativa, Categoria)"
    ]
  },
  {
    number: "4",
    title: "Cargar Benchmarks de Industria",
    description: "Define los valores de referencia del mercado para cada KPI por industria.",
    subItems: [
      "Ir a Benchmarks → Descargar Plantilla",
      "Completar Excel con campos: Industria, Modelo_Negocio, Estructura_Operativa, Categoria, indicador_de_mercado, valor_benchmark, Fuente, agente, Modulo",
      "Subir archivo Excel/CSV",
      "Marcar 'Reemplazar todos' si es carga inicial limpia",
      "Verificar que los benchmarks aparezcan en la tabla con los valores correctos"
    ]
  },
  {
    number: "5",
    title: "Configurar Tipos de Agente por Industria",
    description: "Define configuraciones específicas de agentes según la industria y modelo de negocio.",
    subItems: [
      "Ir a Configuración de Tipos de Agente",
      "Seleccionar la industria a configurar",
      "Definir Sub-industria y Modelo de Negocio",
      "Especificar KPIs sugeridos para esa combinación",
      "Definir Módulos recomendados",
      "Configurar parámetros de análisis específicos del sector",
      "Guardar configuración para cada combinación industria/modelo"
    ]
  },
  {
    number: "6",
    title: "Configurar Agentes Funcionales del Módulo",
    description: "Cada módulo necesita al menos un agente funcional que procese los datos.",
    subItems: [
      "Ir a Configuración de Agentes",
      "Verificar que exista al menos un agente con agent_type: 'functional' para el módulo",
      "Configurar el prompt del agente con instrucciones específicas de análisis",
      "Definir el input_schema (datos que recibe) y output_schema (formato de respuesta)",
      "Asociar el agente al módulo correcto (revenue, supply_chain, operations, etc.)"
    ]
  },
  {
    number: "7",
    title: "Configurar Agentes Premium (Opcional)",
    description: "Los agentes premium generan predicciones y proyecciones basadas en datos históricos.",
    subItems: [
      "Crear agente con agent_type: 'premium' para el módulo",
      "El agente premium recibe los resultados del agente funcional",
      "Configurar prompts para análisis predictivo y tendencias",
      "Definir los KPIs que el agente puede proyectar"
    ]
  },
  {
    number: "8",
    title: "Configurar Agentes C-Level (Opcional)",
    description: "Los agentes C-Level generan insights estratégicos de alto nivel.",
    subItems: [
      "Crear agente con agent_type: 'c_level' para el módulo",
      "Recibe resultados consolidados de agentes funcionales y premium",
      "Configurar prompts para recomendaciones ejecutivas",
      "Define el formato de salida para reportes de dirección"
    ]
  },
  {
    number: "9",
    title: "Configurar el Super Agente SUPER-100",
    description: "El agente de gobernanza que orquesta todos los módulos y genera reportes ejecutivos.",
    subItems: [
      "Ir a Agente de Gobernanza (SUPER-100)",
      "Configurar las Instrucciones Base del agente",
      "Definir el formato de salida HTML (KPI cards, tablas, gráficas)",
      "Especificar los módulos que debe consultar",
      "Configurar cómo debe usar los benchmarks de industria",
      "Definir el estilo de comunicación (ejecutivo, técnico, etc.)",
      "Configurar la generación de visualizaciones (gráficas, indicadores)"
    ]
  },
  {
    number: "10",
    title: "Crear y Configurar el Tenant",
    description: "Crea la empresa/tenant que usará la plataforma.",
    subItems: [
      "Ir a Gestión de Empresas → Nueva Empresa",
      "Ingresar nombre de la empresa y datos de contacto",
      "Asignar el plan correspondiente (determina módulos disponibles)",
      "Crear usuario administrador para el tenant",
      "Activar los módulos que el tenant podrá usar"
    ]
  },
  {
    number: "11",
    title: "El Tenant Configura su Perfil de Industria",
    description: "El administrador del tenant debe seleccionar su clasificación de industria.",
    subItems: [
      "El tenant accede a Perfil de Industria",
      "Selecciona: Industria, Modelo de Negocio, Estructura Operativa, Categoría",
      "El sistema asigna automáticamente KPIs y módulos recomendados",
      "Se vinculan los benchmarks correspondientes a su industria"
    ]
  },
  {
    number: "12",
    title: "El Tenant Realiza Ingesta de Datos",
    description: "El tenant debe cargar sus datos empresariales.",
    subItems: [
      "El tenant va a Ingesta de Datos",
      "Descarga las plantillas para cada módulo",
      "Completa con datos reales de la empresa (ventas, inventario, etc.)",
      "Sube los archivos y verifica la carga",
      "Activa los módulos con datos cargados"
    ]
  },
  {
    number: "13",
    title: "Verificación Final",
    description: "Verifica que todo esté funcionando correctamente.",
    subItems: [
      "El tenant puede ver KPIs en Mission Control",
      "Los KPIs muestran comparación con benchmarks de industria",
      "Puede crear Rutas Estratégicas y recibir análisis del SUPER-100",
      "Las gráficas y visualizaciones se renderizan correctamente",
      "Los insights y reportes se generan sin errores"
    ]
  }
];

// PDF Generation function
const generateChecklistPDF = () => {
  // Create HTML content for PDF
  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Checklist de Configuración - VectorSuite</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; }
    .header h1 { color: #1e40af; font-size: 24px; margin-bottom: 5px; }
    .header p { color: #64748b; font-size: 14px; }
    .checklist-item { margin-bottom: 25px; page-break-inside: avoid; }
    .item-header { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 8px; }
    .item-number { background: #3b82f6; color: white; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; flex-shrink: 0; }
    .item-title { font-size: 16px; font-weight: bold; color: #1e293b; }
    .item-description { color: #64748b; font-size: 13px; margin-left: 40px; margin-bottom: 10px; }
    .sub-items { margin-left: 40px; }
    .sub-item { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 6px; font-size: 13px; }
    .checkbox { width: 14px; height: 14px; border: 1.5px solid #3b82f6; border-radius: 3px; flex-shrink: 0; margin-top: 2px; }
    .sub-item-text { color: #475569; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 12px; }
    .section-divider { border: none; border-top: 1px dashed #cbd5e1; margin: 20px 0; }
    @media print { body { padding: 20px; } .checklist-item { page-break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>📋 Checklist de Configuración de Plataforma</h1>
    <p>VectorSuite - Guía paso a paso para configurar un módulo completo</p>
  </div>
  
  ${checklistData.map(item => `
    <div class="checklist-item">
      <div class="item-header">
        <div class="item-number">${item.number}</div>
        <div class="item-title">${item.title}</div>
      </div>
      <div class="item-description">${item.description}</div>
      <div class="sub-items">
        ${item.subItems.map(subItem => `
          <div class="sub-item">
            <div class="checkbox"></div>
            <span class="sub-item-text">${subItem}</span>
          </div>
        `).join('')}
      </div>
    </div>
  `).join('<hr class="section-divider">')}
  
  <div class="footer">
    <p>Generado por VectorSuite • ${new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
  </div>
</body>
</html>
  `;

  // Create blob and download
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  
  // Open in new window for printing to PDF
  const printWindow = window.open(url, '_blank');
  printWindow.onload = () => {
    printWindow.print();
  };
};

// Checklist Section Component
const ChecklistSection = () => (
  <AccordionItem title="📋 Checklist: Configurar la Plataforma para un Módulo" icon={CheckCircle2} defaultOpen={true}>
    <div className="mb-4 flex items-center justify-between">
      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex-1 mr-4">
        <p className="text-blue-300 text-sm">
          <strong>Este checklist te guía paso a paso</strong> para dejar la plataforma lista para que un tenant pueda utilizar un módulo completo con todos sus agentes y comparaciones de benchmarks.
        </p>
      </div>
      <Button 
        onClick={generateChecklistPDF}
        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Descargar PDF
      </Button>
    </div>

    <div className="space-y-2">
      {checklistData.map((item) => (
        <ChecklistItem 
          key={item.number}
          number={item.number}
          title={item.title}
          description={item.description}
          subItems={item.subItems}
        />
      ))}
    </div>

    <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
      <div className="flex items-center gap-2 text-emerald-400 mb-2">
        <CheckCircle2 className="w-5 h-5" />
        <span className="font-semibold">¡Listo!</span>
      </div>
      <p className="text-slate-300 text-sm">
        Una vez completados todos los pasos, el tenant podrá utilizar el módulo completo con análisis de IA, 
        comparaciones con benchmarks de industria, y reportes ejecutivos generados por el agente SUPER-100.
      </p>
    </div>
  </AccordionItem>
);


// Super Admin Manual Content
const SuperAdminManual = () => (
  <div className="space-y-6">
    {/* Quick Start */}
    <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Rocket className="w-5 h-5 text-blue-400" />
          Inicio Rápido - Super Admin
        </CardTitle>
        <CardDescription className="text-slate-300">
          Guía rápida para comenzar a administrar la plataforma VectorSuite
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FeatureCard 
            icon={Building2}
            title="Gestión de Empresas"
            description="Crea y administra las empresas/tenants que usarán la plataforma"
          />
          <FeatureCard 
            icon={Layers}
            title="Configuración de Agentes"
            description="Personaliza los agentes de IA para cada tipo de industria"
          />
          <FeatureCard 
            icon={BarChart3}
            title="Benchmarks de Industria"
            description="Define los KPIs de referencia por industria y modelo de negocio"
          />
          <FeatureCard 
            icon={Shield}
            title="Agente de Gobernanza"
            description="Configura el agente SUPER-100 para análisis ejecutivos"
          />
        </div>
      </CardContent>
    </Card>

    {/* CHECKLIST DE CONFIGURACIÓN */}
    <ChecklistSection />

    {/* Detailed Sections */}
    <AccordionItem title="Panel de Control (Overview)" icon={BarChart3}>
      <p className="mb-4">
        El panel de control muestra métricas clave de toda la plataforma:
      </p>
      <ul className="list-disc list-inside space-y-2 mb-4">
        <li><strong>Total de Empresas:</strong> Número de tenants activos en la plataforma</li>
        <li><strong>Usuarios Activos:</strong> Usuarios que han accedido recientemente</li>
        <li><strong>Jobs Ejecutados:</strong> Total de análisis realizados por los agentes</li>
        <li><strong>Actividad Reciente:</strong> Timeline de las últimas acciones en la plataforma</li>
      </ul>
    </AccordionItem>

    <AccordionItem title="Gestión de Empresas" icon={Building2}>
      <p className="mb-4">
        Administra las empresas (tenants) que utilizan la plataforma:
      </p>
      <Step number="1" title="Crear Nueva Empresa">
        Haz clic en "Nueva Empresa" e ingresa los datos: nombre, industria, plan y configuración inicial.
      </Step>
      <Step number="2" title="Asignar Usuarios">
        Cada empresa puede tener múltiples usuarios. Asigna el rol de Admin para gestores principales.
      </Step>
      <Step number="3" title="Configurar Módulos">
        Activa los módulos disponibles para cada empresa según su plan y necesidades.
      </Step>
      <Step number="4" title="Monitorear Actividad">
        Revisa el uso, jobs ejecutados y estado de cada empresa desde la lista principal.
      </Step>
    </AccordionItem>

    <AccordionItem title="Configuración de Tipos de Agente" icon={Settings}>
      <p className="mb-4">
        Define cómo se comportan los agentes según el tipo de industria:
      </p>
      <ul className="list-disc list-inside space-y-2 mb-4">
        <li><strong>Industria:</strong> Selecciona la industria objetivo (Retail, Manufactura, etc.)</li>
        <li><strong>Sub-industria:</strong> Especifica el nicho (B2B, B2C, SaaS, etc.)</li>
        <li><strong>Modelo de Negocio:</strong> Define el modelo específico (Suscripción, E-commerce, etc.)</li>
        <li><strong>KPIs Sugeridos:</strong> Lista de métricas relevantes para ese contexto</li>
        <li><strong>Módulos Recomendados:</strong> Módulos más útiles para esa industria</li>
      </ul>
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-4">
        <div className="flex items-center gap-2 text-yellow-400 mb-1">
          <AlertCircle className="w-4 h-4" />
          <span className="font-medium">Importante</span>
        </div>
        <p className="text-sm text-slate-300">
          Los tipos de agente afectan las recomendaciones automáticas cuando una empresa configura su perfil de industria.
        </p>
      </div>
    </AccordionItem>

    <AccordionItem title="Benchmarks de Industria" icon={Target}>
      <p className="mb-4">
        Gestiona los valores de referencia (benchmarks) que se usan para comparar KPIs:
      </p>
      <Step number="1" title="Descargar Plantilla">
        Usa el botón "Plantilla" para obtener el formato Excel/CSV correcto.
      </Step>
      <Step number="2" title="Completar Datos">
        La plantilla incluye: Industria, Sub-Industria, Modelo Negocio, Indicador, Valor, Agente, Módulo, Fuente.
      </Step>
      <Step number="3" title="Cargar Archivo">
        Sube el archivo. Usa "Reemplazar todos" para una carga limpia o deja sin marcar para actualizar/agregar.
      </Step>
      <Step number="4" title="Verificar Carga">
        Revisa la tabla para confirmar que los benchmarks se cargaron correctamente.
      </Step>
    </AccordionItem>

    <AccordionItem title="Agente de Gobernanza (SUPER-100) - Configuración Detallada" icon={Shield}>
      <p className="mb-4">
        El agente SUPER-100 es el corazón de la plataforma. Orquesta todos los módulos y genera reportes ejecutivos completos.
      </p>
      
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
        <h4 className="text-purple-300 font-semibold mb-2">¿Qué hace el SUPER-100?</h4>
        <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">
          <li>Recibe consultas estratégicas del usuario</li>
          <li>Consulta múltiples módulos de análisis (Revenue, Operations, Supply Chain, etc.)</li>
          <li>Compara los resultados con benchmarks de la industria</li>
          <li>Genera reportes HTML visuales con gráficas y KPI cards</li>
          <li>Proporciona recomendaciones estratégicas personalizadas</li>
        </ul>
      </div>

      <h4 className="text-white font-semibold mb-2">Componentes de Configuración:</h4>
      
      <div className="space-y-3 mb-4">
        <div className="bg-slate-800/50 rounded-lg p-3">
          <h5 className="text-blue-400 font-medium mb-1">1. Instrucciones Base (System Prompt)</h5>
          <p className="text-slate-400 text-sm">Define la personalidad, tono y enfoque del agente. Incluye: rol del agente, objetivos, restricciones, y formato esperado de respuesta.</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-lg p-3">
          <h5 className="text-blue-400 font-medium mb-1">2. Formato de Salida HTML</h5>
          <p className="text-slate-400 text-sm">El SUPER-100 genera HTML estructurado que incluye:</p>
          <ul className="list-disc list-inside text-slate-400 text-sm mt-1">
            <li>KPI Cards con colores semáforo (verde/amarillo/rojo)</li>
            <li>Tablas comparativas con benchmarks</li>
            <li>Gráficas embebidas en formato JSON</li>
            <li>Secciones expandibles con análisis detallado</li>
          </ul>
        </div>
        
        <div className="bg-slate-800/50 rounded-lg p-3">
          <h5 className="text-blue-400 font-medium mb-1">3. Formato de Gráficas</h5>
          <p className="text-slate-400 text-sm">Las gráficas se definen en formato JSON con marcadores especiales:</p>
          <code className="block mt-2 p-2 bg-slate-900 rounded text-xs text-emerald-400">
            {`[DATA]10,20,30,40[/DATA] [LABELS]Ene,Feb,Mar,Abr[/LABELS] [BENCH]25,25,25,25[/BENCH]`}
          </code>
        </div>
        
        <div className="bg-slate-800/50 rounded-lg p-3">
          <h5 className="text-blue-400 font-medium mb-1">4. Módulos a Consultar</h5>
          <p className="text-slate-400 text-sm">Especifica qué módulos el SUPER-100 debe consultar para generar el análisis:</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge className="bg-blue-500/20 text-blue-300">Revenue</Badge>
            <Badge className="bg-green-500/20 text-green-300">Supply Chain</Badge>
            <Badge className="bg-purple-500/20 text-purple-300">Operations</Badge>
            <Badge className="bg-orange-500/20 text-orange-300">Finance</Badge>
            <Badge className="bg-pink-500/20 text-pink-300">HR</Badge>
            <Badge className="bg-cyan-500/20 text-cyan-300">Logistics</Badge>
          </div>
        </div>
        
        <div className="bg-slate-800/50 rounded-lg p-3">
          <h5 className="text-blue-400 font-medium mb-1">5. Uso de Benchmarks</h5>
          <p className="text-slate-400 text-sm">Configura cómo el agente debe usar los benchmarks de industria:</p>
          <ul className="list-disc list-inside text-slate-400 text-sm mt-1">
            <li>Comparar cada KPI con su benchmark correspondiente</li>
            <li>Indicar si el KPI está por encima, igual o por debajo del mercado</li>
            <li>Calcular el gap porcentual vs benchmark</li>
            <li>Incluir la fuente del benchmark en el análisis</li>
          </ul>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-4">
        <div className="flex items-center gap-2 text-yellow-400 mb-1">
          <AlertCircle className="w-4 h-4" />
          <span className="font-medium">Importante</span>
        </div>
        <p className="text-sm text-slate-300">
          El SUPER-100 solo funcionará correctamente si: (1) existen agentes funcionales para los módulos, 
          (2) hay benchmarks cargados para la industria del tenant, y (3) el tenant tiene datos ingresados.
        </p>
      </div>
    </AccordionItem>

    <AccordionItem title="Industry Gateway - Taxonomía y Carga Masiva" icon={Layers}>
      <p className="mb-4">
        Configura las taxonomías de industria disponibles en la plataforma:
      </p>
      
      <h4 className="text-white font-semibold mb-2">Estructura de la Taxonomía:</h4>
      <ul className="list-disc list-inside space-y-2 mb-4 text-slate-300">
        <li><strong className="text-white">Industria:</strong> Categoría principal (Retail, Tecnología, Manufactura, Servicios)</li>
        <li><strong className="text-white">Modelo de Negocio:</strong> Cómo opera la empresa (B2C, B2B, SaaS, Franquicia)</li>
        <li><strong className="text-white">Estructura Operativa:</strong> Tipo de operación (Tienda Física, E-commerce, Distribución)</li>
        <li><strong className="text-white">Categoría:</strong> Área específica dentro de la estructura (Ventas, Logística, Marketing)</li>
      </ul>

      <h4 className="text-white font-semibold mb-2">Carga Masiva desde Excel:</h4>
      <p className="text-slate-400 text-sm mb-2">Usa la plantilla con las columnas:</p>
      <code className="block p-2 bg-slate-900 rounded text-xs text-emerald-400 mb-4">
        Industria, Modelo_Negocio, Estructura_Operativa, Categoria
      </code>
      
      <p className="text-slate-400 text-sm">
        El sistema agrupa automáticamente por industria y construye la estructura jerárquica completa.
      </p>
    </AccordionItem>

    {/* AI GOVERNANCE MODULE - DETAILED DOCUMENTATION */}
    <AccordionItem title="Gobernanza de IA - Control de Calidad de Salidas LLM" icon={Shield} defaultOpen={false}>
      <div className="space-y-6">
        {/* Introduction */}
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <h4 className="text-purple-400 font-semibold mb-2 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            ¿Qué es el Módulo de Gobernanza de IA?
          </h4>
          <p className="text-slate-300 text-sm leading-relaxed">
            El Módulo de Gobernanza de IA es un <strong>pipeline de validación y corrección automática</strong> que 
            procesa todas las respuestas generadas por los modelos de lenguaje (LLM) antes de mostrarlas al usuario. 
            Su objetivo principal es <strong>reducir alucinaciones, garantizar consistencia de datos y mejorar la 
            confiabilidad</strong> de los análisis generados por los agentes de IA.
          </p>
        </div>

        {/* Objectives */}
        <div>
          <h4 className="text-white font-semibold mb-3">🎯 Objetivos del Módulo</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-slate-800/50 rounded-lg p-3 border-l-4 border-blue-500">
              <h5 className="text-blue-400 font-medium mb-1">1. Reducir Alucinaciones</h5>
              <p className="text-slate-400 text-xs">
                Detecta cuando el LLM inventa datos que no existen en los archivos fuente del cliente.
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 border-l-4 border-green-500">
              <h5 className="text-green-400 font-medium mb-1">2. Validar Consistencia</h5>
              <p className="text-slate-400 text-xs">
                Verifica que los números y KPIs reportados coincidan con los datos originales del Excel.
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 border-l-4 border-yellow-500">
              <h5 className="text-yellow-400 font-medium mb-1">3. Corrección Automática</h5>
              <p className="text-slate-400 text-xs">
                Si detecta errores, solicita automáticamente al LLM que corrija su respuesta.
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 border-l-4 border-purple-500">
              <h5 className="text-purple-400 font-medium mb-1">4. Monitoreo Continuo</h5>
              <p className="text-slate-400 text-xs">
                Registra métricas de calidad para identificar agentes problemáticos y mejorar prompts.
              </p>
            </div>
          </div>
        </div>

        {/* How it Works */}
        <div>
          <h4 className="text-white font-semibold mb-3">⚙️ ¿Cómo Funciona el Pipeline?</h4>
          <div className="bg-slate-800/30 rounded-lg p-4">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-bold text-sm">1</span>
                </div>
                <div>
                  <h5 className="text-white font-medium">Validación de Schema</h5>
                  <p className="text-slate-400 text-sm">
                    Verifica que la respuesta del LLM tenga la estructura correcta: que incluya KPIs con semáforos (🟢🟡🔴), 
                    etiquetas de datos [DATA] y [BENCH], y formato HTML válido.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-400 font-bold text-sm">2</span>
                </div>
                <div>
                  <h5 className="text-white font-medium">Validación de Consistencia de Datos</h5>
                  <p className="text-slate-400 text-sm">
                    Compara los valores numéricos de la respuesta contra los datos originales del Excel y los benchmarks 
                    de industria. Detecta valores inventados o discrepancias significativas.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-yellow-400 font-bold text-sm">3</span>
                </div>
                <div>
                  <h5 className="text-white font-medium">Repair Loop (Corrección Automática)</h5>
                  <p className="text-slate-400 text-sm">
                    Si se detectan errores, el sistema construye un prompt de corrección que explica al LLM exactamente 
                    qué debe corregir, y solicita una nueva respuesta. Puede intentar hasta 2 reparaciones.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold text-sm">4</span>
                </div>
                <div>
                  <h5 className="text-white font-medium">Cálculo de Confidence Score</h5>
                  <p className="text-slate-400 text-sm">
                    Calcula una puntuación de confianza (0-100%) basada en: validación de schema (40%), consistencia 
                    de datos (40%), ausencia de errores (20%). Se penaliza si requirió reparación.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-400 font-bold text-sm">5</span>
                </div>
                <div>
                  <h5 className="text-white font-medium">Logging y Métricas</h5>
                  <p className="text-slate-400 text-sm">
                    Todos los resultados se guardan en MongoDB para análisis posterior. Incluye errores detectados, 
                    reparaciones realizadas, latencia y score de confianza.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Guide */}
        <div>
          <h4 className="text-white font-semibold mb-3">📊 Guía del Dashboard de Gobernanza</h4>
          <p className="text-slate-400 text-sm mb-4">
            El dashboard tiene 4 pestañas principales. Accede desde <strong>Super Admin → Gobernanza IA</strong>:
          </p>
          
          <div className="space-y-4">
            {/* Tab: Resumen */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h5 className="text-blue-400 font-medium mb-2">📈 Pestaña "Resumen"</h5>
              <p className="text-slate-400 text-sm mb-3">
                Vista general de la salud del sistema de IA:
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-700/50 rounded p-2">
                  <span className="text-white font-medium">Ejecuciones Totales:</span>
                  <p className="text-slate-400">Cantidad de validaciones procesadas</p>
                </div>
                <div className="bg-slate-700/50 rounded p-2">
                  <span className="text-white font-medium">Confianza Promedio:</span>
                  <p className="text-slate-400">Score promedio de todas las ejecuciones (objetivo: &gt;70%)</p>
                </div>
                <div className="bg-slate-700/50 rounded p-2">
                  <span className="text-white font-medium">Tasa de Errores:</span>
                  <p className="text-slate-400">% de respuestas con errores de schema (objetivo: &lt;20%)</p>
                </div>
                <div className="bg-slate-700/50 rounded p-2">
                  <span className="text-white font-medium">Tasa de Reparación:</span>
                  <p className="text-slate-400">% de respuestas que requirieron corrección automática</p>
                </div>
              </div>
            </div>

            {/* Tab: Logs */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h5 className="text-green-400 font-medium mb-2">📋 Pestaña "Logs"</h5>
              <p className="text-slate-400 text-sm mb-3">
                Registro detallado de cada validación:
              </p>
              <ul className="list-disc list-inside text-slate-400 text-sm space-y-1">
                <li><strong className="text-white">Filtros:</strong> Por tipo de agente (Funcional, Premium, C-Level) y estado (con/sin errores)</li>
                <li><strong className="text-white">Columnas:</strong> Fecha, Agente, Tipo, Módulo, Errores Schema, Inconsistencias, Reparación, Confianza</li>
                <li><strong className="text-white">Detalle:</strong> Clic en cualquier log para ver errores específicos y preview del output</li>
              </ul>
            </div>

            {/* Tab: Analytics */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h5 className="text-purple-400 font-medium mb-2">📉 Pestaña "Analíticas"</h5>
              <p className="text-slate-400 text-sm mb-3">
                Análisis agregado para identificar patrones:
              </p>
              <ul className="list-disc list-inside text-slate-400 text-sm space-y-1">
                <li><strong className="text-white">Por Agente:</strong> Identifica qué agentes tienen más errores o menor confianza</li>
                <li><strong className="text-white">Por Módulo:</strong> Compara rendimiento entre Revenue, Finance, Supply Chain, etc.</li>
                <li><strong className="text-white">Tendencias:</strong> Evolución de métricas en los últimos 7, 14, 30 o 90 días</li>
              </ul>
            </div>

            {/* Tab: Config */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h5 className="text-yellow-400 font-medium mb-2">⚙️ Pestaña "Configuración"</h5>
              <p className="text-slate-400 text-sm mb-3">
                Controles del comportamiento del pipeline:
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between bg-slate-700/30 rounded p-2">
                  <div>
                    <span className="text-white font-medium">Repair Loop</span>
                    <p className="text-slate-500 text-xs">Corrección automática mediante segundo LLM call</p>
                  </div>
                  <span className="text-emerald-400">Recomendado: ON</span>
                </div>
                <div className="flex items-center justify-between bg-slate-700/30 rounded p-2">
                  <div>
                    <span className="text-white font-medium">Validación de Datos</span>
                    <p className="text-slate-500 text-xs">Verificar valores contra fuente original</p>
                  </div>
                  <span className="text-emerald-400">Recomendado: ON</span>
                </div>
                <div className="flex items-center justify-between bg-slate-700/30 rounded p-2">
                  <div>
                    <span className="text-white font-medium">Modo Estricto</span>
                    <p className="text-slate-500 text-xs">Rechazar outputs que no pasen validación</p>
                  </div>
                  <span className="text-yellow-400">Opcional: OFF por defecto</span>
                </div>
                <div className="flex items-center justify-between bg-slate-700/30 rounded p-2">
                  <div>
                    <span className="text-white font-medium">Logging a BD</span>
                    <p className="text-slate-500 text-xs">Guardar registros para análisis</p>
                  </div>
                  <span className="text-emerald-400">Recomendado: ON</span>
                </div>
                <div className="flex items-center justify-between bg-slate-700/30 rounded p-2">
                  <div>
                    <span className="text-white font-medium">Intentos de Reparación</span>
                    <p className="text-slate-500 text-xs">Número máximo de intentos de corrección</p>
                  </div>
                  <span className="text-blue-400">Recomendado: 2</span>
                </div>
                <div className="flex items-center justify-between bg-slate-700/30 rounded p-2">
                  <div>
                    <span className="text-white font-medium">Umbral de Confianza</span>
                    <p className="text-slate-500 text-xs">Score mínimo aceptable (0.0 - 1.0)</p>
                  </div>
                  <span className="text-blue-400">Recomendado: 0.7</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How to Interpret Results */}
        <div>
          <h4 className="text-white font-semibold mb-3">🔍 Cómo Interpretar los Resultados</h4>
          
          <div className="space-y-4">
            {/* Confidence Score */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h5 className="text-white font-medium mb-2">Interpretación del Score de Confianza:</h5>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-6 bg-green-500/20 rounded flex items-center justify-center">
                    <span className="text-green-400 text-sm font-bold">80-100%</span>
                  </div>
                  <p className="text-slate-300 text-sm">
                    <strong className="text-green-400">Excelente:</strong> La respuesta pasó todas las validaciones sin errores.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-6 bg-yellow-500/20 rounded flex items-center justify-center">
                    <span className="text-yellow-400 text-sm font-bold">60-79%</span>
                  </div>
                  <p className="text-slate-300 text-sm">
                    <strong className="text-yellow-400">Aceptable:</strong> Hubo advertencias menores o se aplicó reparación exitosa.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-6 bg-red-500/20 rounded flex items-center justify-center">
                    <span className="text-red-400 text-sm font-bold">&lt;60%</span>
                  </div>
                  <p className="text-slate-300 text-sm">
                    <strong className="text-red-400">Revisar:</strong> Errores significativos detectados. Revisar el prompt del agente.
                  </p>
                </div>
              </div>
            </div>

            {/* Common Issues */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h5 className="text-white font-medium mb-2">Problemas Comunes y Soluciones:</h5>
              <div className="space-y-3 text-sm">
                <div className="border-l-4 border-red-500 pl-3">
                  <p className="text-white font-medium">Errores de Schema Altos (&gt;30%)</p>
                  <p className="text-slate-400">
                    El LLM no está siguiendo el formato esperado. <strong>Solución:</strong> Revisar y reforzar las 
                    instrucciones de formato en el prompt del agente. Agregar ejemplos explícitos.
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-3">
                  <p className="text-white font-medium">Inconsistencias de Datos Frecuentes</p>
                  <p className="text-slate-400">
                    El LLM está inventando números. <strong>Solución:</strong> Reforzar en el prompt que solo debe usar 
                    datos etiquetados con [DATA] del Excel. Nunca inventar valores.
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-3">
                  <p className="text-white font-medium">Tasa de Reparación &gt;50%</p>
                  <p className="text-slate-400">
                    El agente necesita muchas correcciones. <strong>Solución:</strong> El prompt es ambiguo. Mejorar 
                    las instrucciones en Prompts Internos para ese tipo de agente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
          <h4 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            Mejores Prácticas
          </h4>
          <ul className="list-disc list-inside text-slate-300 text-sm space-y-1">
            <li>Revisar el dashboard semanalmente para identificar agentes problemáticos</li>
            <li>Mantener Repair Loop activado para corrección automática de errores menores</li>
            <li>Si un agente tiene confianza &lt;60% consistentemente, revisar su prompt en "Prompts Internos"</li>
            <li>Usar las tendencias para detectar degradación de calidad después de cambios</li>
            <li>Activar Modo Estricto solo si la precisión es crítica (puede rechazar respuestas)</li>
          </ul>
        </div>
      </div>
    </AccordionItem>
  </div>
);

// Tenant Manual Content
const TenantManual = () => (
  <div className="space-y-6">
    {/* Quick Start */}
    <Card className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Rocket className="w-5 h-5 text-emerald-400" />
          Inicio Rápido - Dashboard Empresarial
        </CardTitle>
        <CardDescription className="text-slate-300">
          Aprende a usar VectorSuite para potenciar tus decisiones de negocio con IA
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FeatureCard 
            icon={Target}
            title="Mission Control"
            description="Panel centralizado con KPIs, comparaciones vs benchmarks y rutas estratégicas"
          />
          <FeatureCard 
            icon={MessageSquare}
            title="Rutas Estratégicas"
            description="Conversaciones inteligentes para analizar KPIs y obtener recomendaciones"
          />
          <FeatureCard 
            icon={Lightbulb}
            title="Insights"
            description="Análisis generados automáticamente por los agentes de IA"
          />
          <FeatureCard 
            icon={TrendingUp}
            title="Módulos de Análisis"
            description="Agentes especializados en Revenue, Supply Chain, Operations y más"
          />
        </div>
      </CardContent>
    </Card>

    {/* Detailed Sections */}
    <AccordionItem title="Mission Control" icon={Target} defaultOpen={true}>
      <p className="mb-4">
        Tu centro de comando para monitorear el estado de tu negocio:
      </p>
      <ul className="list-disc list-inside space-y-2 mb-4">
        <li><strong>Panel de KPIs:</strong> Visualiza tus métricas vs benchmarks de industria</li>
        <li><strong>Estado de KPIs:</strong> Verde (saludable), Amarillo (atención), Rojo (crítico)</li>
        <li><strong>Comparación con Benchmark:</strong> Entiende cómo te comparas con tu industria</li>
        <li><strong>Crear Ruta Estratégica:</strong> Inicia un análisis profundo de cualquier KPI</li>
      </ul>
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mt-4">
        <div className="flex items-center gap-2 text-emerald-400 mb-1">
          <CheckCircle2 className="w-4 h-4" />
          <span className="font-medium">Tip</span>
        </div>
        <p className="text-sm text-slate-300">
          Haz clic en cualquier KPI para ver el detalle y crear una "Ruta Estratégica" para análisis profundo.
        </p>
      </div>
    </AccordionItem>

    <AccordionItem title="Rutas Estratégicas (Threads)" icon={MessageSquare}>
      <p className="mb-4">
        Las Rutas Estratégicas son conversaciones inteligentes para analizar tu negocio:
      </p>
      <Step number="1" title="Iniciar una Ruta">
        Desde Mission Control, haz clic en un KPI y selecciona "Analizar en Ruta Estratégica", o crea una nueva desde el panel de Threads.
      </Step>
      <Step number="2" title="Análisis Automático">
        El agente SUPER-100 generará un análisis ejecutivo con KPIs, gráficas y recomendaciones.
      </Step>
      <Step number="3" title="Preguntas de Seguimiento">
        Usa el chat para hacer preguntas adicionales sobre el análisis. El agente recordará el contexto.
      </Step>
      <Step number="4" title="Gráficas Interactivas">
        Las respuestas incluyen gráficas de barras, líneas y pie para visualizar los datos.
      </Step>
    </AccordionItem>

    <AccordionItem title="Insights (Análisis Automáticos)" icon={Lightbulb}>
      <p className="mb-4">
        Los insights son análisis generados automáticamente por los agentes:
      </p>
      <ul className="list-disc list-inside space-y-2 mb-4">
        <li><strong>Origen:</strong> Se generan al ejecutar agentes en los módulos</li>
        <li><strong>Contenido:</strong> Incluyen hallazgos, métricas y recomendaciones</li>
        <li><strong>Accionables:</strong> Puedes crear una Ruta Estratégica desde cualquier insight</li>
        <li><strong>Historial:</strong> Todos los insights se guardan para referencia futura</li>
      </ul>
    </AccordionItem>

    <AccordionItem title="Perfil de Industria" icon={Building2}>
      <p className="mb-4">
        Configura tu perfil de industria para obtener análisis más relevantes:
      </p>
      <Step number="1" title="Industria Principal">
        Selecciona tu industria principal (Retail, Manufactura, Tecnología, etc.)
      </Step>
      <Step number="2" title="Sub-industrias">
        Marca las sub-industrias que aplican a tu negocio (puede ser más de una).
      </Step>
      <Step number="3" title="Modelo de Negocio">
        Selecciona tu modelo de negocio principal (B2B, B2C, SaaS, etc.)
      </Step>
      <Step number="4" title="Guardar Cambios">
        Al guardar, el sistema actualizará los KPIs y módulos recomendados automáticamente.
      </Step>
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mt-4">
        <div className="flex items-center gap-2 text-blue-400 mb-1">
          <AlertCircle className="w-4 h-4" />
          <span className="font-medium">Importante</span>
        </div>
        <p className="text-sm text-slate-300">
          El perfil de industria afecta los benchmarks contra los que se comparan tus KPIs y las recomendaciones de los agentes.
        </p>
      </div>
    </AccordionItem>

    <AccordionItem title="Ingesta de Datos" icon={FileText}>
      <p className="mb-4">
        La ingesta de datos te permite cargar información de tu empresa para que los agentes de IA puedan analizarla:
      </p>
      
      <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
        <h4 className="font-medium text-white mb-3">📁 Formatos de Archivo Soportados</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="bg-slate-700/50 rounded px-3 py-2 text-center">
            <span className="text-emerald-400 font-medium">Excel</span>
            <p className="text-xs text-slate-400">.xlsx, .xls</p>
          </div>
          <div className="bg-slate-700/50 rounded px-3 py-2 text-center">
            <span className="text-blue-400 font-medium">CSV</span>
            <p className="text-xs text-slate-400">.csv</p>
          </div>
          <div className="bg-slate-700/50 rounded px-3 py-2 text-center">
            <span className="text-orange-400 font-medium">PDF</span>
            <p className="text-xs text-slate-400">.pdf</p>
          </div>
          <div className="bg-slate-700/50 rounded px-3 py-2 text-center">
            <span className="text-purple-400 font-medium">Texto</span>
            <p className="text-xs text-slate-400">.txt, .json</p>
          </div>
        </div>
      </div>

      <Step number="1" title="Acceder a Ingesta de Datos">
        En el menú lateral, haz clic en "Ingesta de Datos" para abrir el módulo de carga.
      </Step>
      
      <Step number="2" title="Seleccionar Archivo">
        Haz clic en el área de carga o arrastra tu archivo. Puedes cargar archivos Excel, CSV, PDF o texto plano.
      </Step>
      
      <Step number="3" title="Configurar la Carga">
        <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
          <li>Asigna un nombre descriptivo al dataset</li>
          <li>Selecciona el módulo destino (Revenue, Operations, etc.)</li>
          <li>Opcionalmente, agrega etiquetas para organizar</li>
        </ul>
      </Step>
      
      <Step number="4" title="Procesar Datos">
        Haz clic en "Cargar" y espera a que el sistema procese el archivo. El tiempo depende del tamaño del archivo.
      </Step>
      
      <Step number="5" title="Verificar Carga">
        Una vez procesado, verás el dataset en la lista. Los agentes podrán usar esta información en sus análisis.
      </Step>

      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mt-4">
        <div className="flex items-center gap-2 text-emerald-400 mb-1">
          <CheckCircle2 className="w-4 h-4" />
          <span className="font-medium">Tip de Mejores Prácticas</span>
        </div>
        <ul className="text-sm text-slate-300 space-y-1">
          <li>• Usa nombres de columnas descriptivos en tus archivos Excel/CSV</li>
          <li>• Incluye fechas en formato estándar (YYYY-MM-DD)</li>
          <li>• Evita celdas combinadas en Excel</li>
          <li>• Para mejores resultados, sube datos de al menos 3-6 meses</li>
        </ul>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-4">
        <div className="flex items-center gap-2 text-yellow-400 mb-1">
          <AlertCircle className="w-4 h-4" />
          <span className="font-medium">Tipos de Datos Recomendados</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm text-slate-300">
          <div>
            <p className="font-medium text-white">💰 Revenue:</p>
            <p className="text-xs">Ventas, transacciones, clientes, productos</p>
          </div>
          <div>
            <p className="font-medium text-white">📦 Supply Chain:</p>
            <p className="text-xs">Inventario, proveedores, órdenes de compra</p>
          </div>
          <div>
            <p className="font-medium text-white">⚙️ Operations:</p>
            <p className="text-xs">Producción, eficiencia, tiempos de ciclo</p>
          </div>
          <div>
            <p className="font-medium text-white">💵 Finance:</p>
            <p className="text-xs">Estados financieros, flujo de caja, costos</p>
          </div>
        </div>
      </div>
    </AccordionItem>

    <AccordionItem title="Módulos de Análisis" icon={Layers}>
      <p className="mb-4">
        Cada módulo contiene agentes especializados en diferentes áreas:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-slate-800/30 p-3 rounded-lg">
          <h4 className="font-medium text-emerald-400 mb-1">💰 Revenue</h4>
          <p className="text-xs text-slate-400">Análisis de ingresos, ventas, pricing y conversión</p>
        </div>
        <div className="bg-slate-800/30 p-3 rounded-lg">
          <h4 className="font-medium text-blue-400 mb-1">📦 Supply Chain</h4>
          <p className="text-xs text-slate-400">Inventarios, proveedores, logística y cadena de suministro</p>
        </div>
        <div className="bg-slate-800/30 p-3 rounded-lg">
          <h4 className="font-medium text-purple-400 mb-1">⚙️ Operations</h4>
          <p className="text-xs text-slate-400">Eficiencia operativa, procesos y productividad</p>
        </div>
        <div className="bg-slate-800/30 p-3 rounded-lg">
          <h4 className="font-medium text-orange-400 mb-1">💵 Finance</h4>
          <p className="text-xs text-slate-400">Análisis financiero, flujo de caja y rentabilidad</p>
        </div>
      </div>
      <Step number="1" title="Seleccionar Módulo">
        Haz clic en el módulo en la barra lateral para ver sus agentes.
      </Step>
      <Step number="2" title="Ejecutar Agente">
        Selecciona un agente y haz clic en "Ejecutar" para generar un análisis.
      </Step>
      <Step number="3" title="Ver Resultados">
        El análisis aparecerá como un nuevo insight que puedes explorar y expandir.
      </Step>
    </AccordionItem>

    <AccordionItem title="Cómo Interpretar las Gráficas" icon={BarChart3}>
      <p className="mb-4">
        Las gráficas te ayudan a visualizar los datos de forma clara:
      </p>
      <ul className="list-disc list-inside space-y-2 mb-4">
        <li><strong>Gráfica de Barras:</strong> Ideal para comparar valores (KPI actual vs Benchmark)</li>
        <li><strong>Gráfica de Líneas:</strong> Muestra tendencias y evolución en el tiempo</li>
        <li><strong>Gráfica Circular (Pie):</strong> Distribución porcentual de categorías</li>
      </ul>
      <div className="bg-slate-800/30 p-3 rounded-lg">
        <h4 className="font-medium text-white mb-2">Colores en las barras:</h4>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-500"></div>
            <span className="text-slate-300">Valor Actual</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-slate-300">Benchmark</span>
          </div>
        </div>
      </div>
    </AccordionItem>

    <AccordionItem title="Preguntas Frecuentes (FAQ)" icon={HelpCircle}>
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-white mb-1">¿Cómo se calculan los benchmarks?</h4>
          <p className="text-sm text-slate-400">
            Los benchmarks son valores de referencia de tu industria, configurados por el administrador de la plataforma basados en estudios de mercado y mejores prácticas.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-white mb-1">¿Cada cuánto se actualizan los análisis?</h4>
          <p className="text-sm text-slate-400">
            Los análisis se generan bajo demanda cuando ejecutas un agente o creas una Ruta Estratégica. Puedes ejecutarlos las veces que necesites.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-white mb-1">¿Puedo exportar los reportes?</h4>
          <p className="text-sm text-slate-400">
            Actualmente puedes copiar el contenido de los análisis. La función de exportación a PDF estará disponible próximamente.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-white mb-1">¿Las gráficas no se muestran?</h4>
          <p className="text-sm text-slate-400">
            Las gráficas se generan automáticamente cuando el agente incluye datos visualizables. Si no aparecen, intenta hacer una nueva pregunta solicitando específicamente una gráfica comparativa.
          </p>
        </div>
      </div>
    </AccordionItem>
  </div>
);

// Main UserManualModule Component
const UserManualModule = ({ userRole = 'tenant' }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/10 rounded-lg">
            <Book className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Manual de Usuario</h2>
            <p className="text-slate-400 text-sm">
              {userRole === 'super_admin' 
                ? 'Guía completa para administradores de la plataforma'
                : 'Aprende a sacar el máximo provecho de VectorSuite'
              }
            </p>
          </div>
        </div>
        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
          v1.0
        </Badge>
      </div>

      {userRole === 'super_admin' ? <SuperAdminManual /> : <TenantManual />}
    </div>
  );
};

export default UserManualModule;
